import React, { useRef } from 'react';
import { Pie } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';
import { ChartControls, LoadingSpinner } from '../ui';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors, getChartBackgroundColor, mergeChartOptions } from '../../utils/themeHelpers';
import { formatNumber } from '../../utils/formatters';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { useRefineCustomerComplaintStatus } from '../../hooks/useRefineCustomerQueries';

interface ComplaintReviewStatusProps {
  branchId?: number;
  showLoading?: boolean;
  onError?: (error: Error) => void;
}

const ComplaintReviewStatus: React.FC<ComplaintReviewStatusProps> = ({
  branchId = 0,
  showLoading = true,
  onError
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { theme: currentTheme } = useTheme();
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  
  // Component metadata
  const componentId = 'complaint-review-status';
  const componentTitle = 'وضعیت بررسی شکایات';
  
  // Fetch data using individual hook
  const { data: complaintStatus, isLoading, error } = useRefineCustomerComplaintStatus(branchId);

  // Handle error
  React.useEffect(() => {
    if (error && onError) {
      onError(error as Error);
    }
  }, [error, onError]);

  if (isLoading && showLoading) {
    return (
       <LoadingSpinner
              variant="minimal"
              size="lg"
              message="در حال بارگذاری…"
            />
    );
  }

  if (error || !complaintStatus) {
    return null;
  }

  // Chart colors based on theme
  const chartColors = getChartColors(currentTheme);

  const complaintStatusData = {
    labels: ['بررسی شده', 'بررسی نشده'],
    datasets: [{
      data: [
        complaintStatus.reviewed || 0,
        complaintStatus.notReviewed || 0
      ],
      backgroundColor: [
        getChartBackgroundColor(0, currentTheme, 0.8), // Green for reviewed
        getChartBackgroundColor(1, currentTheme, 0.8)  // Red for not reviewed
      ],
      borderColor: [chartColors[0], chartColors[1]],
      borderWidth: 1
    }]
  };

  // Pie chart options using theme helpers
  const pieChartOptions: ChartOptions<'pie'> = mergeChartOptions(getChartJsOptions(currentTheme), {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        display: false
      },
      y: {
        display: false
      }
    },
    plugins: {
      legend: {
        // Any pie-specific legend overrides can go here
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const separator = '\u00A0:\u00A0';
            const formattedValue = `${value}%`;
            return `${label}${separator}${formattedValue}`;
          }
        }
      }
    }
  });

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <ChartControls
        componentId={componentId}
        componentTitle={componentTitle}
        componentType="chart"
        dashboardSource="refineCustomer"
        onInfo={() => openGuide(CHART_GUIDES['complaint-status'])}
        onFavorite={() => handleFavoriteToggle({
          componentId,
          componentTitle,
          dashboardSource: 'refineCustomer',
          componentType: 'chart',
          description: 'نمودار دایره‌ای وضعیت بررسی شکایات',
          props: { branchId, showLoading, onError },
          gridLayout: {
            containerClass: 'card-base card-padding'
          }
        })}
        isFavorited={isFavorited(componentId)}
        chartRef={chartRef}
        className="mb-4"
      />
      <h3 className="title-card mb-4">{componentTitle}</h3>
      <div style={{ height: 300, width: '100%' }}>
        <Pie data={complaintStatusData} options={pieChartOptions} />
      </div>
      <div className="mt-4 text-center">
        <div className="inline-flex gap-4">
          <span className="px-3 py-1 rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
            بررسی شده: {formatNumber(complaintStatus.reviewed || 0)}
          </span>
          <span className="px-3 py-1 rounded-full bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
            بررسی نشده: {formatNumber(complaintStatus.notReviewed || 0)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ComplaintReviewStatus;